import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { PollWithDetails, PollOptionWithVotes, PollVote, PollOption } from '../types';

interface SearchablePoll extends PollWithDetails {
  chat_name?: string;
}

/**
 * Hook to search polls across all chats the user is part of
 */
export const useSearchPolls = (userId: string | undefined, searchQuery: string) => {
  const [polls, setPolls] = useState<SearchablePoll[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchPolls = useCallback(async () => {
    if (!userId || !searchQuery.trim() || searchQuery.length < 2) {
      setPolls([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const query = searchQuery.toLowerCase().trim();

      // Fetch polls from chats the user is part of
      const { data, error: fetchError } = await supabase
        .from('polls')
        .select(`
          *,
          creator:profiles!polls_created_by_fkey(id, full_name, nickname, avatar_url),
          chat:chats!inner(id, name, type, participants:chat_participants!inner(user_id)),
          options:poll_options(
            *,
            votes:poll_votes(*)
          )
        `)
        .or(`question.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (fetchError) throw fetchError;

      if (data) {
        // Filter to only include polls from chats the user is a participant of
        // and also search in options
        const userPolls = data.filter((poll: any) => {
          // Check if user is participant
          const isParticipant = poll.chat?.participants?.some(
            (p: any) => p.user_id === userId
          );
          if (!isParticipant) return false;

          // Check if question matches
          if (poll.question.toLowerCase().includes(query)) return true;

          // Check if any option matches
          const optionMatch = poll.options?.some((opt: any) =>
            opt.option_text.toLowerCase().includes(query)
          );
          return optionMatch;
        });

        // Transform data
        const transformedPolls: SearchablePoll[] = userPolls.map((poll: any) => {
          const options: PollOptionWithVotes[] = (poll.options || [])
            .sort((a: PollOption, b: PollOption) => a.option_order - b.option_order)
            .map((option: PollOption & { votes: PollVote[] }) => ({
              ...option,
              vote_count: option.votes?.length || 0,
              voted_by_current_user:
                option.votes?.some((v: PollVote) => v.user_id === userId) || false,
            }));

          const totalVotes = options.reduce((sum, opt) => sum + opt.vote_count, 0);
          const hasVoted = options.some((opt) => opt.voted_by_current_user);

          return {
            ...poll,
            options,
            total_votes: totalVotes,
            has_voted: hasVoted,
            creator: poll.creator,
            chat_name: poll.chat?.name || 'Chat',
          };
        });

        setPolls(transformedPolls);
      }
    } catch (err) {
      console.error('Error searching polls:', err);
      setError(err instanceof Error ? err.message : 'Failed to search polls');
      setPolls([]);
    } finally {
      setLoading(false);
    }
  }, [userId, searchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchPolls();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchPolls]);

  return {
    polls,
    loading,
    error,
  };
};

